/**
 * CR229: T2 - Deal Card Editor Top Bar
 * 
 * Replaces the left rail with a space-efficient top bar layout.
 * Contains deal type selector, template status, and actions.
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertCircle,
  ChevronDown,
  Copy,
  FilePlus,
  Plus,
  Save,
  Undo2,
  RefreshCw,
} from "lucide-react";
import { RIBBON_THEME_CLASSES, type DealTypeRegistryEntry } from "@/providers/DealCardConfigProvider";
import type { TemplateSource } from "@/lib/deals/templateResolver";

interface DealCardEditorTopBarProps {
  // Deal type selection
  dealTypes: DealTypeRegistryEntry[];
  selectedType: string;
  onTypeChange: (type: string) => void;
  
  // Template status
  templateSource: TemplateSource;
  fallbackFrom?: string;
  hasUnsavedChanges: boolean;
  hasDraft: (type: string) => boolean;
  
  // Actions
  onSave: () => void;
  onDiscard: () => void;
  onRefresh: () => void;
  onCreateFromDefault: () => void;
  onDuplicateFrom: (sourceType: string) => void;
  
  // State
  saving: boolean;
  loading: boolean;
}

export function DealCardEditorTopBar({
  dealTypes,
  selectedType,
  onTypeChange,
  templateSource,
  fallbackFrom,
  hasUnsavedChanges,
  hasDraft,
  onSave,
  onDiscard,
  onRefresh,
  onCreateFromDefault,
  onDuplicateFrom,
  saving,
  loading,
}: DealCardEditorTopBarProps) {
  const otherTypes = dealTypes.filter(dt => dt.deal_type_key !== selectedType);
  
  return (
    <div className="flex items-center gap-3 p-3 bg-muted/50 border-b rounded-t-lg flex-wrap">
      {/* Deal Type Selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-muted-foreground">Deal Type:</span>
        <Select value={selectedType} onValueChange={onTypeChange}>
          <SelectTrigger className="w-[180px] h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {dealTypes.map((entry) => {
              // CR321: Always derive dot color from registry entry's ribbon_theme (SSOT)
              const themeClass = RIBBON_THEME_CLASSES[entry.ribbon_theme] || "bg-gray-400";
              return (
                <SelectItem key={entry.deal_type_key} value={entry.deal_type_key}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${themeClass}`} />
                    <span>{entry.label}</span>
                    {hasDraft(entry.deal_type_key) && (
                      <Badge variant="secondary" className="ml-1 text-[9px] px-1 py-0">modified</Badge>
                    )}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      <Separator orientation="vertical" className="h-6" />

      {/* Template Status */}
      <div className="flex items-center gap-2">
        {templateSource === "fallback" ? (
          <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 gap-1">
            <AlertCircle className="h-3 w-3" />
            Using {fallbackFrom || "DEFAULT"}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-emerald-600 border-emerald-300 bg-emerald-50">
            Custom Template
          </Badge>
        )}
        
        {hasUnsavedChanges && (
          <Badge variant="destructive" className="gap-1">
            <AlertCircle className="h-3 w-3" />
            Unsaved
          </Badge>
        )}
      </div>

      <div className="flex-1" />

      {/* Actions */}
      <div className="flex items-center gap-2">
        {/* Create/Duplicate Actions */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1">
              <Plus className="h-3.5 w-3.5" />
              Actions
              <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {templateSource === "fallback" && (
              <DropdownMenuItem onClick={onCreateFromDefault} className="gap-2">
                <FilePlus className="h-4 w-4" />
                Create template for {selectedType}
              </DropdownMenuItem>
            )}
            
            <DropdownMenuSeparator />
            
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
              Duplicate from...
            </div>
            {otherTypes.map((dt) => (
              <DropdownMenuItem 
                key={dt.deal_type_key}
                onClick={() => onDuplicateFrom(dt.deal_type_key)}
                className="gap-2"
              >
                <Copy className="h-4 w-4" />
                Copy from {dt.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Separator orientation="vertical" className="h-6" />

        {/* Refresh */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
          className="gap-1"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>

        {/* Discard */}
        <Button
          variant="outline"
          size="sm"
          onClick={onDiscard}
          disabled={saving || loading || !hasUnsavedChanges}
          className="gap-1"
        >
          <Undo2 className="h-3.5 w-3.5" />
          Discard
        </Button>

        {/* Save */}
        <Button
          size="sm"
          onClick={onSave}
          disabled={saving || !hasUnsavedChanges}
          className="gap-1 min-w-[80px]"
        >
          {saving ? (
            <>
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-3.5 w-3.5" />
              Save
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
